import * as React from 'react';
import { ExperimentContext } from './ExperimentContext';
import { OverrideExperimentContext } from './OverrideExperimentContext';
import { Experiment } from './types';

// noContextDefault is used when the expIs is not present in expContext, in the below useExperiment function
// if it is true, the experiment is true
export interface ExperimentDeterminationConfig {
    expId: string;
    unseen?: true;
    appIds: string[];
    noContextDefault?: boolean;
}

export interface WhitelabelExperimentDeterminationConfig {
    expId: string;
    unseen?: true;
    whitelabelIds: string[];
    noContextDefault?: boolean;
}

export type ExperimentObject<TExperimentKey extends string> = Record<TExperimentKey, ExperimentDeterminationConfig>;

export type WhitelabelExperimentObject<TExperimentKey extends string> = Record<
    TExperimentKey,
    WhitelabelExperimentDeterminationConfig
>;

export type ExperimentResult<TExperimentKey extends string> = Record<TExperimentKey, boolean>;

export function useExperiment<TExpKey extends string>(
    expObj: ExperimentObject<TExpKey> | WhitelabelExperimentObject<TExpKey>
): ExperimentResult<TExpKey> {
    const expContext = React.useContext(ExperimentContext);
    const overrideExpContext = React.useContext(OverrideExperimentContext);

    return Object.entries<ExperimentDeterminationConfig | WhitelabelExperimentDeterminationConfig>(expObj).reduce(
        (acc, [expKey, { expId, noContextDefault }]) => {
            // if not production environment, check override experiments first
            if (__ENV__ !== 'prod') {
                const { explist, expuser } = overrideExpContext;
                // check from explist in parent URL
                if (explist && expId in explist) {
                    acc[expKey as TExpKey] = explist[expId].toUpperCase() === 'B';
                } else if (expId in expContext) {
                    // check from explist in iframe URL
                    acc[expKey as TExpKey] = determineExperiment(expContext, expId, noContextDefault);
                } else {
                    // check from expUser in iframe URL
                    acc[expKey as TExpKey] = expuser ? expuser.toUpperCase() === 'B' : !!noContextDefault;
                }
            } else {
                acc[expKey as TExpKey] = determineExperiment(expContext, expId, noContextDefault);
            }
            return acc;
        },
        {} as ExperimentResult<TExpKey>
    );
}

function determineExperiment(expContext: Experiment, expId: string, noContextDefault: boolean | undefined): boolean {
    return isExpIdExists(expContext, expId) ? isB(expContext, expId) : !!noContextDefault;
}

export const isB = (expContext: Experiment, expId: string): boolean => {
    return isExpIdExists(expContext, expId) && expContext[expId].toUpperCase() === 'B';
};

const isExpIdExists = (expContext: Experiment, expId: string): boolean => {
    return expId in expContext;
};
